import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Layers, Lock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { refreshFlags } from "@/hooks/useFlags";
import { Switch } from "@/components/ui/switch";

export function FeatureFlagsPanel() {
  const { t } = useTranslation();
  const [flags, setFlags] = useState({ v2_enabled: false, v3_enabled: false, v4_enabled: false });

  useEffect(() => { api.get("/feature-flags").then((r) => setFlags(r.data)).catch(() => {}); }, []);

  const save = async (next) => {
    setFlags(next);
    try { await api.put("/admin/feature-flags", next); await refreshFlags(); toast.success(t("versions.saved")); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const Row = ({ vkey, title, desc, locked, disabled, onChange }) => (
    <div className="flex items-center justify-between bg-white rounded-2xl border border-border p-5">
      <div className="pr-4">
        <p className="font-heading font-bold text-ink">{title}</p>
        <p className="text-sm text-muted2 font-body mt-0.5">{desc}</p>
      </div>
      {locked ? <Lock className="w-5 h-5 text-muted2" /> : (
        <Switch data-testid={`flag-${vkey}`} checked={flags[`${vkey}_enabled`]} disabled={disabled}
          onCheckedChange={onChange || ((v) => save({ ...flags, [`${vkey}_enabled`]: v }))} />
      )}
    </div>
  );

  return (
    <div data-testid="versions-panel" className="space-y-4 max-w-2xl">
      <p className="text-sm text-muted2 font-body flex items-center gap-2"><Layers className="w-4 h-4 text-canard" /> {t("versions.intro")}</p>
      <Row vkey="v1" title={t("versions.v1")} desc={t("versions.v1desc")} locked />
      <Row vkey="v2" title={t("versions.v2")} desc={(flags.v3_enabled || flags.v4_enabled) ? `${t("versions.v2desc")} · ${t("versions.v2locked")}` : t("versions.v2desc")} disabled={flags.v3_enabled || flags.v4_enabled} />
      <Row vkey="v3" title={t("versions.v3")} desc={flags.v4_enabled ? `${t("versions.v3desc")} · ${t("versions.v3locked")}` : t("versions.v3desc")} disabled={flags.v4_enabled}
        onChange={(v) => save({ ...flags, v3_enabled: v, v2_enabled: v ? true : flags.v2_enabled, v4_enabled: v ? flags.v4_enabled : false })} />
      <Row vkey="v4" title={t("versions.v4")} desc={t("versions.v4desc")}
        onChange={(v) => save({ ...flags, v4_enabled: v, v3_enabled: v ? true : flags.v3_enabled, v2_enabled: v ? true : flags.v2_enabled })} />
    </div>
  );
}
